// src/components/ProcessManager/CommercialRegister/ProcessManagerCommercialRegisterResult.tsx
import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { IProcessManager } from '../../Interfaces/IProcessManager';
import type { IDocument } from '../../Interfaces/IDocument';
import { DocumentCard } from '../../DocumentPortal/DocumentSortingDialog/DocumentCard';

interface Props {
  processManager: IProcessManager & { idProcessManagerType: 20 | 21 };
}

function parseToDate(input: any): Date | null {
  if (!input) return null;
  if (input instanceof Date && !isNaN(input.getTime())) return input;

  if (typeof input === 'string') {
    let s = input.trim();

    // "YYYY-MM-DD HH:mm:ss" → ISO
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) {
      s = s.replace(' ', 'T');
    }

    // "YYYY-MM-DD" → lokale Mitternacht
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split('-').map(Number);
      return new Date(y, m - 1, d, 0, 0, 0, 0);
    }

    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
}

function formatDateTime(input: any): string {
  const d = parseToDate(input);
  if (!d) return '—';
  const date = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
  const time = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  return `${date} ${time} Uhr`;
}

function formatDateOnly(input: any): string {
  const d = parseToDate(input);
  if (!d) return '—';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

export const ProcessManagerCommercialRegisterResult: React.FC<Props> = ({
  processManager,
}) => {
  const rawData = (processManager as any)?.data;
  const d: any =
    typeof rawData === 'string'
      ? (() => {
          try {
            return JSON.parse(rawData);
          } catch {
            return {};
          }
        })()
      : (rawData ?? {});

  const lastChange = d?.lastChange;
  const dk = d?.dkLastDetection;

  const idDocument = d?.idDocument != null ? Number(d.idDocument) : undefined;

  const type = processManager.idProcessManagerType;
  const showTradeRegister = type === 20;
  const showShareholderList = type === 21;

  const initialDocument: IDocument | undefined = idDocument
    ? {
        idDocument,
        FileName: `Dokument ${idDocument}`,
        Data: undefined,
        idMIMEType: 1,
        Created_at: '',
        idLegalTransaction: null,
        idDocumentDirectory: null,
        perEmail: null,
        isFromTemplate: null,
      }
    : undefined;

  return (
    <Stack spacing={2}>
      {/* Status */}
      <Box>
        <Typography variant='h6' gutterBottom>
          Status
        </Typography>
        <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
          <Chip
            label={`Status: ${processManager.idProcessManagerState === 1 ? 'offen' : 'erledigt'}`}
          />
          <Chip label={`Versuche: ${processManager.Tries ?? 0}`} />
          {processManager.LastTry_at && (
            <Chip
              label={`Letzter Versuch: ${formatDateTime(processManager.LastTry_at)}`}
            />
          )}
          {processManager.Closed_at && (
            <Chip
              color='success'
              label={`Geschlossen: ${formatDateTime(processManager.Closed_at)}`}
            />
          )}
          {processManager.ExitCodeLastTry != null && (
            <Chip label={`ExitCode: ${processManager.ExitCodeLastTry}`} />
          )}
        </Stack>
      </Box>

      {(showTradeRegister || showShareholderList || idDocument) && <Divider />}

      {/* Letzte Handelsregister-Änderung (nur Typ 20) */}
      {showTradeRegister && (
        <Box>
          <Typography variant='h6' gutterBottom>
            Letzte Handelsregister-Änderung (SI)
          </Typography>
          {lastChange ? (
            <Stack spacing={0.5}>
              <Typography variant='body2'>
                Eintragsdatum: {formatDateOnly(lastChange.entry_date)}
              </Typography>
              <Typography variant='body2'>
                Aktenzeichen: {lastChange.aktenzeichen ?? '—'}
              </Typography>
              <Typography variant='body2'>
                Erkannt am: {formatDateTime(lastChange.detected_at)}
              </Typography>
            </Stack>
          ) : (
            <Typography variant='body2'>—</Typography>
          )}
        </Box>
      )}

      {showShareholderList && idDocument && <Divider />}

      {/* Gesellschafterliste (nur Typ 21) */}
      {showShareholderList && (
        <Box>
          <Typography variant='h6' gutterBottom>
            Gesellschafterliste (DK)
          </Typography>
          {dk ? (
            <Stack spacing={0.5}>
              <Typography variant='body2'>
                Neue Dokumente: {dk.new_count ?? 0}
              </Typography>
              {dk.max_doc_date && (
                <Typography variant='body2'>
                  Stand: {formatDateOnly(dk.max_doc_date)}
                </Typography>
              )}
              <Typography variant='body2'>
                Erkannt am: {formatDateTime(dk.detected_at)}
              </Typography>
            </Stack>
          ) : (
            <Typography variant='body2'>—</Typography>
          )}
        </Box>
      )}

      {idDocument && <Divider />}

      {/* Hinterlegtes Dokument  */}
      {initialDocument ? (
        <Box>
          <Typography variant='h6' gutterBottom>
            Hinterlegtes Dokument (AD)
          </Typography>
          <Box sx={{ maxWidth: 380 }}>
            <DocumentCard
              initialDocument={initialDocument}
              selectable={false}
            />
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
};
